import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const blank = { name: "", category: "raw", unit: "kg", quantity: 0, reorder_level: 0, supplier: "" };

export default function AdminInventory() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/inventory").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { ...editing, quantity: Number(editing.quantity), reorder_level: Number(editing.reorder_level) };
    delete body.id; delete body.created_at;
    try {
      if (editing.id) await api.put(`/admin/inventory/${editing.id}`, body);
      else await api.post(`/admin/inventory`, body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/inventory/${id}`); load(); };

  return (
    <div data-testid="admin-inventory" className="space-y-8">
      <header className="flex items-end justify-between">
        <div><div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Inventory</div><h1 className="editorial-h1 text-5xl mt-2">{items.length} items tracked.</h1></div>
        <button onClick={() => { setEditing({ ...blank }); setOpen(true); }} data-testid="inv-new" className="btn-pill btn-dark text-sm"><Plus size={16} /> Add Item</button>
      </header>
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm" data-testid="inv-table">
          <thead className="text-left text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">
            <tr className="border-b border-brand-line"><th className="p-4">Item</th><th className="p-4">Category</th><th className="p-4">Stock</th><th className="p-4">Reorder</th><th className="p-4">Supplier</th><th className="p-4"></th></tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const low = i.quantity <= i.reorder_level;
              return (
                <tr key={i.id} className="border-b border-brand-line last:border-0" data-testid={`inv-${i.id}`}>
                  <td className="p-4 font-medium flex items-center gap-2">{low && <AlertTriangle size={14} className="text-brand-red" />}{i.name}</td>
                  <td className="p-4 text-xs uppercase tracking-[0.18em] text-brand-charcoal/60">{i.category}</td>
                  <td className="p-4">{i.quantity} {i.unit}</td>
                  <td className="p-4 text-brand-charcoal/65">{i.reorder_level} {i.unit}</td>
                  <td className="p-4 text-brand-charcoal/65">{i.supplier || "—"}</td>
                  <td className="p-4 flex gap-2"><button onClick={() => { setEditing(i); setOpen(true); }} className="btn-pill btn-ghost text-xs border border-brand-line">Edit</button><button onClick={() => del(i.id)} className="btn-pill btn-ghost text-xs"><Trash2 size={14} /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {open && editing && (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/40 grid place-items-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg p-8">
            <div className="flex items-center justify-between mb-4"><div className="font-display text-3xl">{editing.id ? "Edit item" : "New item"}</div><button onClick={() => setOpen(false)}><X /></button></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Name</label><input data-testid="inv-f-name" className="pp-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Category</label>
                <select data-testid="inv-f-cat" className="pp-input bg-transparent" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  <option value="raw">raw</option><option value="packaging">packaging</option><option value="supplies">supplies</option><option value="finished">finished</option>
                </select>
              </div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Unit</label>
                <select data-testid="inv-f-unit" className="pp-input bg-transparent" value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })}>
                  <option value="kg">kg</option><option value="l">l</option><option value="pcs">pcs</option><option value="boxes">boxes</option>
                </select>
              </div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Quantity</label><input data-testid="inv-f-qty" type="number" className="pp-input" value={editing.quantity} onChange={(e) => setEditing({ ...editing, quantity: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Reorder level</label><input data-testid="inv-f-re" type="number" className="pp-input" value={editing.reorder_level} onChange={(e) => setEditing({ ...editing, reorder_level: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Supplier</label><input data-testid="inv-f-sup" className="pp-input" value={editing.supplier || ""} onChange={(e) => setEditing({ ...editing, supplier: e.target.value })} /></div>
            </div>
            <div className="mt-6 flex gap-3 justify-end"><button onClick={() => setOpen(false)} className="btn-pill btn-ghost text-sm">Cancel</button><button onClick={save} data-testid="inv-f-save" className="btn-pill btn-dark text-sm">Save</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
